import { fechaObs } from './freshness';
import { categoriaLabel, fuenteLabel, presentacionLabel, rubroLabel } from './labels';
import { roundTo } from './numbers';
import type {
  AlertaMetrica,
  ISODate,
  Lang,
  Precio,
  PreferenciasConfig,
  SerieId,
  SerieKey,
  SerieRef,
} from './types';

/* ───────────────────────────────── Identidad de series ───────────────────────────────── */

/** `rubro|categoria|presentacion` (presentación vacía si es null). */
export const serieId = (key: SerieKey): SerieId =>
  `${key.rubro}|${key.categoria}|${key.presentacion ?? ''}`;

export function parseSerieId(id: string): SerieKey | null {
  const [rubro, categoria, presentacion, ...resto] = id.split('|');
  if (!rubro || !categoria || presentacion === undefined || resto.length > 0) return null;
  return { rubro, categoria, presentacion: presentacion || null };
}

export const serieDe = (p: SerieKey): SerieKey => ({
  rubro: p.rubro,
  categoria: p.categoria,
  presentacion: p.presentacion,
});

/** Serie en una región y fuente concretas (la unidad de comparación de variaciones y alertas). */
export const serieCompletaId = (
  p: SerieKey & { region: string | null; fuente: string | null },
): string => `${serieId(p)}|${p.region ?? ''}|${p.fuente ?? ''}`;

export interface GrupoSerie {
  id: SerieId;
  serie: SerieKey;
  precios: Precio[];
}

/** Agrupa por serie conservando el orden de primera aparición. */
export function agruparPorSerie(precios: readonly Precio[]): GrupoSerie[] {
  const grupos = new Map<SerieId, GrupoSerie>();
  for (const precio of precios) {
    const id = serieId(precio);
    const grupo = grupos.get(id) ?? { id, serie: serieDe(precio), precios: [] };
    grupo.precios.push(precio);
    grupos.set(id, grupo);
  }
  return [...grupos.values()];
}

/* ───────────────────────────────── Filtros y búsqueda ───────────────────────────────── */

/** `undefined` = sin filtro · `null` = solo filas SIN ese dato · lista vacía = sin filtro · lista = cualquiera de. */
type Valor = string | null | ReadonlyArray<string | null> | undefined;

export interface FiltrosPrecios {
  rubro?: Valor;
  categoria?: Valor;
  presentacion?: Valor;
  region?: Valor;
  fuente?: Valor;
  /** Límites inclusivos sobre la fecha de observación (fechaFin ?? fecha). */
  desde?: ISODate | null;
  hasta?: ISODate | null;
}

function coincide(valor: string | null, filtro: Valor): boolean {
  if (filtro === undefined) return true;
  if (filtro === null || typeof filtro === 'string') return valor === filtro;
  return filtro.length === 0 || filtro.includes(valor);
}

export function filtrarPrecios(precios: readonly Precio[], f: FiltrosPrecios): Precio[] {
  return precios.filter((p) => {
    if (!coincide(p.rubro, f.rubro) || !coincide(p.categoria, f.categoria)) return false;
    if (!coincide(p.presentacion, f.presentacion) || !coincide(p.region, f.region)) return false;
    if (!coincide(p.fuente, f.fuente)) return false;
    const obs = fechaObs(p);
    return (!f.desde || obs >= f.desde) && (!f.hasta || obs <= f.hasta);
  });
}

/** Aplica la lista blanca de intereses de las preferencias (vacío = todo). */
export const filtrarPorIntereses = (
  precios: readonly Precio[],
  i: PreferenciasConfig['intereses'],
): Precio[] => filtrarPrecios(precios, { rubro: i.rubros, region: i.regiones, fuente: i.fuentes });

const sinAcentos = (texto: string): string =>
  texto.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase();

const pajares = new WeakMap<Precio, string>();

function pajar(p: Precio): string {
  let texto = pajares.get(p);
  if (texto === undefined) {
    const etiquetas = (['es', 'en'] as const).flatMap((lang) => [
      rubroLabel(p.rubro, lang),
      categoriaLabel(p.categoria, lang),
      p.presentacion ? presentacionLabel(p.presentacion, lang) : '',
    ]);
    texto = sinAcentos(
      [
        p.rubro,
        p.categoria,
        p.presentacion ?? '',
        p.region ?? '',
        fuenteLabel(p.fuente),
        ...etiquetas,
      ].join(' '),
    );
    pajares.set(p, texto);
  }
  return texto;
}

/** Búsqueda por texto: ignora mayúsculas y tildes, busca en claves y etiquetas es/en; todas las palabras deben aparecer. */
export function buscarPrecios(precios: readonly Precio[], texto: string): Precio[] {
  const palabras = sinAcentos(texto).split(/\s+/).filter(Boolean);
  if (palabras.length === 0) return [...precios];
  return precios.filter((p) => palabras.every((palabra) => pajar(p).includes(palabra)));
}

/* ───────────────────────────────── Orden y valores únicos ───────────────────────────────── */

export type OrdenPrecios =
  'nombre' | 'precio_asc' | 'precio_desc' | 'fecha_desc' | 'fecha_asc' | 'region' | 'fuente';

const collators: Record<Lang, Intl.Collator> = {
  es: new Intl.Collator('es', { sensitivity: 'base', numeric: true }),
  en: new Intl.Collator('en', { sensitivity: 'base', numeric: true }),
};

export function ordenarPrecios(
  precios: readonly Precio[],
  orden: OrdenPrecios = 'nombre',
  lang: Lang = 'es',
): Precio[] {
  const c = collators[lang].compare;
  const nombre = (a: Precio, b: Precio): number =>
    c(rubroLabel(a.rubro, lang), rubroLabel(b.rubro, lang)) ||
    c(categoriaLabel(a.categoria, lang), categoriaLabel(b.categoria, lang)) ||
    c(a.presentacion ?? '', b.presentacion ?? '') ||
    c(a.region ?? '', b.region ?? '') ||
    c(a.fuente, b.fuente);
  const criterios: Record<OrdenPrecios, (a: Precio, b: Precio) => number> = {
    nombre,
    precio_asc: (a, b) => a.prom - b.prom || nombre(a, b),
    precio_desc: (a, b) => b.prom - a.prom || nombre(a, b),
    fecha_desc: (a, b) => c(fechaObs(b), fechaObs(a)) || nombre(a, b),
    fecha_asc: (a, b) => c(fechaObs(a), fechaObs(b)) || nombre(a, b),
    region: (a, b) => c(a.region ?? '', b.region ?? '') || nombre(a, b),
    fuente: (a, b) => c(a.fuente, b.fuente) || nombre(a, b),
  };
  return [...precios].sort(criterios[orden]);
}

export type CampoUnico = 'rubro' | 'categoria' | 'presentacion' | 'region' | 'fuente' | 'unidad';

/** Valores distintos (sin nulos) de un campo, en orden alfabético. */
export function valoresUnicos(precios: readonly Precio[], campo: CampoUnico): string[] {
  const valores = new Set<string>();
  for (const p of precios) {
    const v = p[campo];
    if (v) valores.add(v);
  }
  return [...valores].sort(collators.es.compare);
}

/* ───────────────────────────────── Valores y Bs ───────────────────────────────── */

export const valorMetrica = (
  p: { min: number; max: number; prom: number },
  metrica: AlertaMetrica,
): number => p[metrica];

/** Fila más reciente (por fechaObs, luego fecha) que cumple la referencia; ante empate gana la primera recibida. */
export function filaMasReciente(precios: readonly Precio[], ref: SerieRef): Precio | null {
  const candidatas = filtrarPrecios(precios, {
    rubro: ref.rubro,
    categoria: ref.categoria,
    presentacion: ref.presentacion,
    region: ref.region ?? undefined,
    fuente: ref.fuente ?? undefined,
  });
  let mejor: Precio | null = null;
  for (const p of candidatas) {
    if (!mejor) mejor = p;
    else {
      const a = fechaObs(p);
      const b = fechaObs(mejor);
      if (a > b || (a === b && p.fecha > mejor.fecha)) mejor = p;
    }
  }
  return mejor;
}

export interface RangoBs {
  min: number | null;
  max: number | null;
  prom: number | null;
}

const aBs = (usd: number, tasa: number | null | undefined): number | null =>
  tasa && Number.isFinite(tasa) && tasa > 0 ? roundTo(usd * tasa, 2) : null;

/**
 * Bs de un precio según la preferencia del usuario:
 *  - 'actual': precio × tasa BCV de hoy,
 *  - 'fecha_dato': los Bs que guardó el servidor con la tasa de la fecha del dato (si faltan, precio × tasa de esa fecha;
 *    y si tampoco hay, la tasa de hoy).
 * Si el precio ya está en bolívares se devuelve tal cual. null cuando no hay ninguna tasa utilizable.
 */
export function bsDe(
  precio: Precio,
  bsModo: PreferenciasConfig['bsModo'],
  tasaActual: number | null | undefined,
): RangoBs {
  if (/^(ves|bs)/i.test(precio.moneda))
    return { min: precio.min, max: precio.max, prom: precio.prom };
  if (bsModo === 'actual') {
    return {
      min: aBs(precio.min, tasaActual),
      max: aBs(precio.max, tasaActual),
      prom: aBs(precio.prom, tasaActual),
    };
  }
  const tasa = precio.tasaBcv ?? tasaActual;
  return {
    min: precio.minBs ?? aBs(precio.min, tasa),
    max: precio.maxBs ?? aBs(precio.max, tasa),
    prom: precio.promBs ?? aBs(precio.prom, tasa),
  };
}
